<template>
    <div class="feature-editor">
        <div class="feature-editor__body">
            <or-textbox
                v-model="$v.localFeature.$model.name"
                :label="$t('model.nlu-model.sidebar.features.editor.featureeditor.name.label')"
                :placeholder="$t('model.nlu-model.sidebar.features.editor.featureeditor.name.placeholder')"
                enforceMaxlength
                :invalid="!!featureErrors.name"
                :error="featureErrors.name"
                :readonly="$props.isLoading"
                :disabled="status.fetching || status.deleting || status.updating"
            />
            <or-chips
                class="feature-editor__words-list"
                :value="$v.localFeature.$model.words"
                :label="$t('model.nlu-model.sidebar.features.editor.featureeditor.values')"
                :simpleText="true"
                :invalid="!!featureErrors.words"
                :error="featureErrors.words"
                :disabled="status.fetching || status.deleting || status.updating"
                @input="updateFeatureValues"
            />
            <or-checkbox
                class="feature-option"
                v-model="$v.localFeature.$model.enabledForAllModels"
                color="accent"
            >
              {{ $t('model.nlu-model.sidebar.features.editor.featureeditor.globalApplyTo') }}
            </or-checkbox>
            <or-checkbox
                class="feature-option"
                v-model="$v.localFeature.$model.mode"
                color="accent"
            >
              {{ $t('model.nlu-model.sidebar.features.editor.featureeditor.theseValuesAreInterchangeable') }}
            </or-checkbox>
        </div>

        <div class="feature-editor__footer">
            <or-button
                v-show="!isNewFeature"
                class="delete-btn"
                type="secondary"
                color="red"
                :disabled="status.fetching || status.deleting || status.updating || status.creating"
                :loading="status.deleting"
                @click="$refs.deleteFeatureConfirm.open()"
            >
              {{ $t('generic.delete') }}
            </or-button>

            <or-button
                class="cancel-btn"
                color="primary"
                type="secondary"
                disabledRipple
                :disabled="status.fetching || status.deleting || status.updating || status.creating"
                @click="$emit('cancel')"
            >
              {{ $t('generic.cancel') }}
            </or-button>

            <or-button
                color="primary"
                class="modal-btn"
                disabledRipple
                :disabled="status.fetching || status.deleting || status.updating || status.creating"
                :loading="status.fetching || status.updating || status.creating"
                @click="onConfirm"
            >
                {{ !isNewFeature ? $t('generic.save') : $t('generic.create') }}
            </or-button>
        </div>
        <or-confirm
            ref="deleteFeatureConfirm"
            :title="$t('model.nlu-model.sidebar.features.editor.featureeditor.deleteFeatureConfirm.title')"
            @confirm="deleteFeatureHandler"
        >
          {{ $t('model.nlu-model.sidebar.features.editor.featureeditor.deleteFeatureConfirm.description') }}
        </or-confirm>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { featureValidation } from '@/mixins/features/feature-validation.mixin'

export const getDefaultFeature = () => ({
  name: '',
  words: [],
  enabledForAllModels: true,
  mode: false
})

export default {
  name: 'FeatureEditor',
  mixins: [featureValidation],
  props: {
    selectedFeature: Object,
    isNewFeature: Boolean
  },

  data () {
    return {
      localFeature: _.cloneDeep(this.$props.selectedFeature),
      isLoading: false,
      isDeleting: false
    }
  },

  computed: {
    ...mapState('knowledgebase/features', ['status'])
  },

  methods: {
    ...mapActions('knowledgebase/features', ['createFeature', 'updateFeature', 'deleteFeature']),

    async onConfirm () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        console.debug('[onConfirm] Was not able to save:', this.$v.localFeature)
        return
      }

      try {
        if (this.$props.isNewFeature) {
          await this.createFeature(this.localFeature)
        } else {
          await this.updateFeature(this.localFeature)
        }
        this.$emit('cancel')
      } catch (e) {
        console.log("Wasn't able to save:", e)
      }
    },

    async deleteFeatureHandler () {
      await this.deleteFeature(this.localFeature)
      this.$emit('cancel')
    },

    updateFeatureValues (featureWords) {
      this.localFeature.words = featureWords.map(word => word.replace(/`/g, ''))
    }
  }
}
</script>

<style scoped lang="scss">
    .feature-editor {
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;

        .feature-editor__body {
            flex-grow: 1;

            .feature-editor__words-list {
                ::v-deep .ui-chips__wrapper {
                    max-height: 400px;
                    min-height: 120px;
                    align-content: flex-start;
                    overflow-y: auto;
                }
            }

            .feature-option {
                margin: $sp-3 0;
            }

            ::v-deep .ui-checkbox--color-accent.is-checked > .ui-checkbox__checkmark::before {
                background-color: rgb(64, 159, 236);
                border-color: rgb(64, 159, 236);
            }
        }

        .feature-editor__footer {
            display: flex;
            justify-content: flex-end;
            margin-top: $sp-3;
            padding: $sp-3;
            border-top: solid 1px $or-grey-medium;

            .cancel-btn {
                margin-right: $sp-3;
            }

            .delete-btn {
                margin-right: auto;
            }
        }
    }
</style>
